import { cn, Input } from "@tellescope/ui";
import { TellescopeFormFieldBaseProps } from "../../types";
import { useTranslation } from "react-i18next";
import { useForm } from "../../../use-form";
import { StateSelectField } from "./StateSelectField";

export interface AddressFieldProps extends TellescopeFormFieldBaseProps<"Address"> {}

export const AddressField = ({ title, value, onChange, className, error, onBlur }: AddressFieldProps) => {
  const { t } = useTranslation("forms");

  const form = useForm({
    fields: {
      addressLineOne: {
        type: "text",
        initialValue: value?.addressLineOne,
        required: true,
      },
      addressLineTwo: {
        type: "text",
        initialValue: value?.addressLineTwo,
      },
      city: {
        type: "text",
        initialValue: value?.city,
        required: true,
      },
      state: {
        type: "select",
        initialValue: value?.state,
        required: true,
      },
      zipCode: {
        type: "text",
        initialValue: value?.zipCode,
        required: true,
      },
    },
    validators: {
      zipCode: (val) => {
        if (!val) return undefined;
        return /^\d{5}(-\d{4})?$/.test(val) ? undefined : t("Please enter a valid zip code");
      },
    },
    sideEffects: (newVal) => {
      onChange(newVal);
      return newVal;
    },
    validateOn: "blur",
  });

  const getFieldProps: typeof form.getFieldProps = (fieldName) => {
    const props = form.getFieldProps(fieldName);
    return {
      ...props,
      onBlur: () => {
        props.onBlur();
        onBlur?.();
      },
    };
  };

  const getCustomFieldProps: typeof form.getCustomFieldProps = (fieldName) => {
    const props = form.getCustomFieldProps(fieldName);
    return {
      ...props,
      onBlur: () => {
        props.onBlur();
        onBlur?.();
      },
    };
  };

  return (
    <div className={cn("space-y-2", className)}>
      {!!title && <h4>{title}</h4>}
      <div className="flex flex-col gap-1">
        <Input {...getFieldProps("addressLineOne")} placeholder={t("Address Line 1")} />
        <Input {...getFieldProps("addressLineTwo")} placeholder={t("Address Line 2")} />
        <div className="flex gap-1">
          <Input {...getFieldProps("city")} placeholder={t("City")} className="flex-1" />
          <StateSelectField {...getCustomFieldProps("state")} className="flex-1" />
          <Input {...getFieldProps("zipCode")} placeholder={t("Zip Code")} className="flex-1" />
        </div>
      </div>
      {!!error && <p className="text-sm text-destructive mt-1">{error}</p>}
    </div>
  );
};
